import React, { useEffect, useState } from 'react';
import currency from 'helpers/currency';
import date from 'helpers/date';
import { Eye, EyeOff, Activity, DollarSign, Clock, ArrowDownCircle, ChevronDown } from 'lucide-react';
import styled from 'styled-components';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';

const VISIBILITY_STORAGE_KEY = 'financial:values-visible';

function readStoredVisibility() {
    try {
        return window.localStorage.getItem(VISIBILITY_STORAGE_KEY) === 'true';
    } catch (error) {
        return false;
    }
}

export default function StatisticsCards({ data }) {
    const [isVisible, setIsVisible] = useState(readStoredVisibility);
    const [showBreakdown, setShowBreakdown] = useState(false);

    useEffect(() => {
        try {
            window.localStorage.setItem(VISIBILITY_STORAGE_KEY, String(isVisible));
        } catch (error) {
            /* Navegador com storage bloqueado: a preferência só não persiste. */
        }
    }, [isVisible]);

    function money(value) {
        return isVisible ? currency.format(value || 0) : 'R$ *****';
    }

    const hasBreakdown = Boolean(
        data?.total_transactions_pending ||
        data?.total_transactions_canceled ||
        data?.total_transactions_refunded
    );

    return (
        <>
            <VisibilityBar>
                <VisibilityToggle
                    onClick={() => setIsVisible(v => !v)}
                    aria-pressed={isVisible}
                    aria-label={isVisible ? 'Ocultar valores' : 'Mostrar valores'}
                >
                    {isVisible ? <EyeOff size={15} /> : <Eye size={15} />}
                    <span>{isVisible ? 'Ocultar valores' : 'Mostrar valores'}</span>
                </VisibilityToggle>
            </VisibilityBar>

            <Container>
                <Card accentColor={colors.primary}>
                    <CardTop>
                        <IconBox bgColor={`${colors.primary}14`}>
                            <Activity size={18} color={colors.primary} />
                        </IconBox>
                        <Label>Pedidos aprovados</Label>
                    </CardTop>
                    <Value color={colors.primaryDark}>
                        {data?.count_total_transacted || 0}
                    </Value>
                    <Subtext>Pedidos pagos no período</Subtext>
                </Card>

                <Card accentColor={colors.gold}>
                    <CardTop>
                        <IconBox bgColor={`${colors.gold}14`}>
                            <DollarSign size={18} color={colors.gold} />
                        </IconBox>
                        <Label>Total transacionado</Label>
                    </CardTop>
                    <Value color={colors.goldDark}>
                        {money(data?.total_transactions_carried_out)}
                    </Value>

                    {hasBreakdown ? (
                        <>
                            <BreakdownToggle
                                onClick={() => setShowBreakdown(v => !v)}
                                aria-expanded={showBreakdown}
                            >
                                <span>O que não entrou aqui</span>
                                <Chevron open={showBreakdown}>
                                    <ChevronDown size={13} />
                                </Chevron>
                            </BreakdownToggle>

                            {showBreakdown && (
                                <Breakdown>
                                    <BreakdownRow>
                                        <span>Aguardando pagamento</span>
                                        <strong>{money(data?.total_transactions_pending)}</strong>
                                    </BreakdownRow>
                                    <BreakdownRow>
                                        <span>Expirado</span>
                                        <strong>{money(data?.total_transactions_canceled)}</strong>
                                    </BreakdownRow>
                                    <BreakdownRow>
                                        <span>Estornado</span>
                                        <strong>{money(data?.total_transactions_refunded)}</strong>
                                    </BreakdownRow>
                                </Breakdown>
                            )}
                        </>
                    ) : (
                        <Subtext>Somente pedidos aprovados</Subtext>
                    )}
                </Card>

                <Card accentColor={colors.occasionalPurple}>
                    <CardTop>
                        <IconBox bgColor={`${colors.occasionalPurple}12`}>
                            <Clock size={18} color={colors.occasionalPurple} />
                        </IconBox>
                        <Label>Total a receber</Label>
                    </CardTop>
                    <Value color={colors.secondaryPurple}>
                        {money(data?.balance_pending)}
                    </Value>

                    {data?.balance_pending_releasing_soon > 0 && (
                        <Highlight>
                            {money(data?.balance_pending_releasing_soon)} liberam em até 7 dias
                        </Highlight>
                    )}

                    <Subtext>
                        {data?.next_release_at
                            ? `Próxima liberação em ${date.format(data.next_release_at)}`
                            : 'Sem liberação agendada'}
                    </Subtext>
                </Card>

                <Card accentColor={colors.success}>
                    <CardTop>
                        <IconBox bgColor={`${colors.success}15`}>
                            <ArrowDownCircle size={18} color={colors.success} />
                        </IconBox>
                        <Label>Disponível para saque</Label>
                    </CardTop>
                    <Value color={colors.successDark}>
                        {money(data?.balance_available)}
                    </Value>
                    <Subtext>Pronto para sacar</Subtext>
                </Card>
            </Container>
        </>
    );
}

const VisibilityBar = styled.div`
    display: flex;
    justify-content: flex-end;
    padding-top: 10px;
`;

const VisibilityToggle = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    border-radius: 8px;
    cursor: pointer;
    color: ${colors.night};
    font-size: 12px;
    font-family: inherit;
    transition: background 0.15s ease;

    &:hover {
        background: ${colors.neutral};
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const Container = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 10px 0;
    align-items: start;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(4, 1fr);
    }
`;

const Card = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 20px;
    border-left: 3px solid ${({ accentColor }) => accentColor};
    box-shadow: 0 1px 3px ${colors.night}0A;
    transition: box-shadow 0.2s ease, transform 0.2s ease;

    &:hover {
        box-shadow: 0 4px 12px ${colors.night}12;
        transform: translateY(-1px);
    }
`;

const CardTop = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
`;

const IconBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${({ bgColor }) => bgColor};
    flex-shrink: 0;
`;

const Label = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
    line-height: 1.3;
`;

const Value = styled.div`
    font-size: 24px;
    font-weight: 700;
    color: ${({ color }) => color || colors.nightDark};
    letter-spacing: -0.5px;
    line-height: 1.2;
`;

const Subtext = styled.span`
    display: block;
    font-size: 12px;
    color: ${colors.nightLight};
    margin-top: 8px;
`;

const Highlight = styled.span`
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.secondaryPurple};
    margin-top: 8px;
`;

const BreakdownToggle = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 8px;
    padding: 0;
    border: none;
    background: transparent;
    color: ${colors.nightLight};
    font-size: 12px;
    font-family: inherit;
    cursor: pointer;

    &:hover {
        color: ${colors.night};
    }

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const Chevron = styled.span`
    display: flex;
    transition: transform 0.2s ease;
    transform: rotate(${({ open }) => open ? '180deg' : '0deg'});
`;

const Breakdown = styled.div`
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid ${colors.neutralDark};
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const BreakdownRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 12px;
    color: ${colors.nightLight};

    strong {
        font-weight: 600;
        color: ${colors.night};
    }
`;
